import React, { useState } from "react";
import { Layout } from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link2, Bitcoin, Landmark, Loader2, Package, ArrowRight } from "lucide-react";

const BRANDS = {
  temu: { label: "Temu", tagline: "Everyday finds, delivered through MOVA.", image: "https://images.pexels.com/photos/5868722/pexels-photo-5868722.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600", accent: "Orange value picks, gadgets and home" },
  shein: { label: "Shein", tagline: "Trend-led fashion, ordered for you.", image: "https://images.unsplash.com/photo-1483985988355-763728e1935b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", accent: "Dresses, sets and accessories" },
};

export const ShopCenter = ({ brand }) => {
  const cfg = BRANDS[brand];
  const { user, setAuthOpen } = useApp();
  const [form, setForm] = useState({ product_url: "", title: "", size: "", qty: 1, notes: "", payment_method_id: "" });
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);

  const { data: methods = [] } = useQuery({ queryKey: ["payment-methods"], queryFn: async () => (await api.get("/payment-methods")).data });
  const { data: myOrders = [], refetch } = useQuery({
    queryKey: ["orders", user?.user_id],
    queryFn: async () => (await api.get("/orders")).data,
    enabled: !!user,
  });

  const selectedMethod = methods.find((m) => m.id === form.payment_method_id);

  const place = async () => {
    if (!user) { toast("Sign in to place an order"); setAuthOpen(true); return; }
    if (!form.product_url.trim()) { toast.error("Paste the product link"); return; }
    if (!form.payment_method_id) { toast.error("Choose a payment method"); return; }
    setPlacing(true);
    try {
      const res = await api.post("/orders", { brand, ...form, qty: Number(form.qty) || 1 });
      setPlaced({ order: res.data, method: selectedMethod });
      toast.success("Order request placed");
      setForm({ product_url: "", title: "", size: "", qty: 1, notes: "", payment_method_id: "" });
      refetch();
    } catch (e) {
      toast.error("Could not place order");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <Layout>
      <PageHeader eyebrow={`${cfg.label} Center`} title={cfg.tagline} subtitle={`Paste any ${cfg.label} product link and MOVA orders it for you. ${cfg.accent}.`} image={cfg.image} />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-5 gap-8" data-testid={`shop-center-${brand}`}>
        <div className="lg:col-span-3 rounded-3xl border border-white/10 bg-[#101017] p-8">
          <h3 className="font-serif text-2xl text-white mb-6">Order from {cfg.label}</h3>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Product link</label>
              <div className="mt-2 flex items-center gap-2 bg-[#14141c] border border-amber-400/15 rounded-xl px-4">
                <Link2 size={16} className="text-amber-400/70" />
                <input value={form.product_url} onChange={(e) => setForm({ ...form, product_url: e.target.value })} placeholder={`Paste ${cfg.label} product URL`} data-testid="order-url-input" className="flex-1 bg-transparent py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Item name (optional)" data-testid="order-title-input" className="bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none" />
              <input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="Size / variant" data-testid="order-size-input" className="bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input type="number" min={1} value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} placeholder="Qty" data-testid="order-qty-input" className="bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-3 text-sm text-white focus:outline-none" />
            </div>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (colour, preferences…)" rows={3} data-testid="order-notes-input" className="w-full bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none resize-none" />

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Payment method</label>
              {methods.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No payment methods available yet — the admin will add crypto and bank options soon.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {methods.map((m) => (
                    <button key={m.id} onClick={() => setForm({ ...form, payment_method_id: m.id })} data-testid={`payment-method-${m.id}`} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${form.payment_method_id === m.id ? "border-amber-400/60 bg-amber-400/10" : "border-white/10 hover:border-amber-400/30"}`}>
                      {m.kind === "crypto" ? <Bitcoin size={18} className="text-champagne" /> : <Landmark size={18} className="text-champagne" />}
                      <div>
                        <p className="text-sm text-white">{m.label}</p>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">{m.kind}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button onClick={place} disabled={placing} data-testid="place-order-btn" className="w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] py-3.5 font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
              {placing ? <Loader2 className="animate-spin" size={18} /> : <>Place order request <ArrowRight size={16} /></>}
            </button>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {placed && (
            <div className="rounded-3xl border border-amber-400/30 bg-[#13130d] p-6" data-testid="payment-instructions">
              <h4 className="font-serif text-xl text-champagne">Payment instructions</h4>
              <p className="text-sm text-slate-400 mt-2">Complete payment via <span className="text-white">{placed.method?.label}</span> ({placed.method?.kind}):</p>
              <div className="mt-3 p-3 rounded-xl bg-black/40 border border-white/10 text-sm text-slate-200 break-words">{placed.method?.details}</div>
              {placed.method?.note && <p className="text-xs text-slate-500 mt-2">{placed.method.note}</p>}
              <p className="text-xs text-amber-400/80 mt-4">Your order is <strong>pending</strong>. Once payment is confirmed, our team fulfils it. (Card checkout via Stripe coming soon.)</p>
            </div>
          )}

          <div className="rounded-3xl border border-white/10 bg-[#101017] p-6">
            <div className="flex items-center gap-2 mb-4 text-champagne"><Package size={18} /><h4 className="font-serif text-xl">Your {cfg.label} orders</h4></div>
            {!user ? <p className="text-sm text-slate-500">Sign in to see your orders.</p> : myOrders.filter((o) => o.brand === brand).length === 0 ? <p className="text-sm text-slate-500">No orders yet.</p> : (
              <div className="space-y-3">
                {myOrders.filter((o) => o.brand === brand).map((o) => (
                  <div key={o.id} className="p-3 rounded-xl bg-[#15151d] border border-white/5" data-testid={`order-${o.id}`}>
                    <p className="text-sm text-white truncate">{o.title || o.product_url}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-slate-500">Qty {o.qty}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-400/15 text-champagne capitalize">{o.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};
